/* global process */
import { Button } from '@praetorian-chariot/ui';
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * GraphErrorBoundary - Error Boundary for Graph Visualization
 *
 * Catches and handles errors in the graph visualization component tree,
 * providing a graceful fallback UI and error reporting.
 *
 * Features:
 * - Catches React rendering errors in graph components
 * - Provides user-friendly error message
 * - Allows retry functionality
 * - Logs errors to console in development
 * - Can be integrated with error tracking services (Sentry, etc.)
 *
 * @example
 * <GraphErrorBoundary>
 *   <NodeGraph />
 * </GraphErrorBoundary>
 */
export class GraphErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Graph visualization error:', error, errorInfo);
    }

    // TODO: Log to error tracking service (Sentry, LogRocket, etc.)
    // Example: Sentry.captureException(error, { extra: errorInfo });

    this.setState({
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="bg-layer0 flex h-full w-full flex-col items-center justify-center gap-4 p-8">
          <div className="text-center">
            <h3 className="text-default text-lg font-semibold">
              Graph visualization failed to load
            </h3>
            <p className="text-default-dark mt-2 text-sm">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
              <details className="mt-4 max-w-2xl text-left">
                <summary className="text-default-dark hover:text-default cursor-pointer text-sm">
                  Error Details (Development Only)
                </summary>
                <pre className="bg-layer1 mt-2 overflow-auto rounded p-4 text-xs">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={this.handleReset}
              styleType="primary"
              size="md"
              label="Retry"
            />
            <Button
              onClick={() => window.location.reload()}
              styleType="secondary"
              size="md"
              label="Reload Page"
            />
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
